import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { Modal } from './Modal';
import { albums } from './api';
import type { TrackSummary } from '../tracks/TrackList';

/**
 * Pick from your own uploads and add them to an album.
 *
 * Lists the owner's tracks rather than offering a search, because an album is
 * built from your own catalogue and that is a list you can simply show. A
 * search box over your own thirty tracks is a control that makes the common
 * case slower.
 *
 * Tracks already on the album stay in the list, disabled and marked. Removing
 * them would make the list shuffle under the cursor as you add things - and
 * seeing what is already on it is useful.
 */
export function AddTracksDialog({
  albumId,
  username,
  alreadyOn,
  onClose,
  onAdded,
}: {
  albumId: number;
  username: string;
  alreadyOn: Set<number>;
  onClose: () => void;
  onAdded: () => void;
}) {
  // Tracks added in this dialog, so rows update without a refetch per click.
  const [added, setAdded] = useState<Set<number>>(new Set());
  const [failed, setFailed] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['tracks', username],
    queryFn: () => api.get<{ items: TrackSummary[] }>(`/users/${username}/tracks`),
  });

  const add = useMutation({
    mutationFn: (trackId: number) => albums.addTrack(albumId, trackId),
    onSuccess: (_result, trackId) => {
      setAdded((current) => new Set(current).add(trackId));
      onAdded();
    },
    onError: () => setFailed('That track could not be added. Try again.'),
  });

  /*
   * Only tracks that finished processing.
   *
   * A pending or failed track has no audio behind it, and putting one on an
   * album produces a row that cannot play. The upload page is where somebody
   * waits for that; this is not the place to surface it.
   */
  const candidates = (data?.items ?? []).filter((track) => track.status === 'ready');

  return (
    <Modal title={copy['cta.addTracks']} onClose={onClose}>
      {isPending ? (
        <div className="flex flex-col gap-2 py-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : candidates.length === 0 ? (
        <p className="py-2 text-sm text-soft">
          You have no finished tracks yet. Upload something first and it will show up here.
        </p>
      ) : (
        <>
          {failed && (
            <div className="mb-4">
              <Alert tone="error">{failed}</Alert>
            </div>
          )}

          <ul className="flex flex-col gap-1" data-add-track-list>
            {candidates.map((track) => {
              const on = alreadyOn.has(track.id) || added.has(track.id);

              return (
                <li
                  key={track.id}
                  data-add-track={track.id}
                  className="flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-2 hover:bg-raised"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-ink">{track.title}</p>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span className="meta-caps">{duration(track.duration_ms)}</span>
                      {track.visibility !== 'public' && (
                        <Chip>{track.visibility === 'private' ? 'Private' : 'Unlisted'}</Chip>
                      )}
                    </div>
                  </div>

                  {on ? (
                    <span className="meta-caps shrink-0 text-positive" data-added>
                      On album
                    </span>
                  ) : (
                    <Button
                      variant="secondary"
                      className="shrink-0 px-3 py-1.5"
                      loading={add.isPending && add.variables === track.id}
                      onClick={() => {
                        setFailed(null);
                        add.mutate(track.id);
                      }}
                    >
                      Add
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className="mt-5 border-t border-line pt-4">
        <Button onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}

function duration(ms: number | null): string {
  if (ms === null) return '—';

  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
